import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  ClientOnly,
} from "@tanstack/react-router";
import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "sonner";

import appCss from "../styles.css?url";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SiteBreadcrumbs } from "@/components/SiteBreadcrumbs";
import { CookieConsent } from "@/components/CookieConsent";
import { supabase } from "@/integrations/supabase/client";
import { AutoAds } from "@/components/AutoAds";
import { InstallAppPrompt } from "@/components/InstallAppPrompt";
import { ScrollToTopOnNavigate } from "@/components/ScrollToTopOnNavigate";
import { MobileCallBar } from "@/components/MobileCallBar";
import { BackToTop } from "@/components/BackToTop";

// Påskägget är rent kosmetiskt — lazy-laddas på klienten efter hydration så
// det aldrig ligger i initial-bundlen.
const SecretHusbilParade = lazy(() =>
  import("@/components/SecretHusbilParade").then((m) => ({ default: m.SecretHusbilParade })),
);

const NOT_FOUND_LINKS: { to: string; label: string }[] = [
  { to: "/husbilar-till-salu", label: "Husbilar till salu" },
  { to: "/husvagnar-till-salu", label: "Husvagnar till salu" },
  { to: "/rapido", label: "Rapido" },
  { to: "/reservdelar", label: "Reservdelar" },
  { to: "/campingtips", label: "Campingtips" },
  { to: "/blogg", label: "Blogg" },
  { to: "/guider", label: "Guider" },
  { to: "/kontakt", label: "Kontakt" },
];

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-warm px-4">
      <div className="max-w-xl text-center">
        <p className="eyebrow">404</p>
        <h1 className="mt-4 font-display text-6xl md:text-8xl">
          Sidan finns <span className="font-italic-display text-accent">inte</span>.
        </h1>
        <p className="mt-4 text-sm text-muted-foreground">Sidan har flyttats eller existerar inte längre. Prova någon av våra populära sidor:</p>
        <ul className="mt-6 flex flex-wrap justify-center gap-2">
          {NOT_FOUND_LINKS.map((l) => (
            <li key={l.to}>
              <Link
                to={l.to}
                className="inline-flex rounded-full border border-border bg-card px-4 py-2 text-sm transition hover:bg-secondary"
              >
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
        <Link to="/" className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90">
          Till startsidan →
        </Link>
      </div>
    </div>
  );
}


function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  return (
    <div className="flex min-h-screen items-center justify-center bg-warm px-4">
      <div className="max-w-md text-center">
        <p className="eyebrow">Error</p>
        <h1 className="mt-3 font-display text-4xl md:text-5xl">
          Något gick <span className="font-italic-display text-accent">fel</span>.
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">Försök igen om en stund.</p>
        <div className="mt-8 flex justify-center gap-3">
          <button onClick={() => { router.invalidate(); reset(); }} className="rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">Försök igen</button>
          <a href="/" className="rounded-full border border-border bg-card px-5 py-3 text-sm font-medium hover:bg-secondary">Hem</a>
        </div>
      </div>
    </div>
  );
}

const ORGANIZATION_JSONLD = {
  "@context": "https://schema.org",
  "@type": "AutoDealer",
  "@id": "#organization",
  name: "AK Husbil & Fritid",
  alternateName: "AK Husbil och Fritid",
  description:
    "Familjeägd handlare av husbilar, husvagnar och fritidstillbehör i Kalix sedan 2004.",
  url: "https://www.akhusbilfritid.se/",
  telephone: "+46703045344",
  email: "info@akhusbilfritid.se",
  priceRange: "$$",
  foundingDate: "2004",
  areaServed: ["Kalix", "Norrbotten", "Sverige"],
  address: {
    "@type": "PostalAddress",
    streetAddress: "Stabsvägen 5",
    postalCode: "952 51",
    addressLocality: "Kalix",
    addressRegion: "Norrbotten",
    addressCountry: "SE",
  },
  geo: { "@type": "GeoCoordinates", latitude: 65.8557, longitude: 23.1545 },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      description: "Enligt överenskommelse — ring 070-862 97 43 för att boka besök",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    },
  ],
  sameAs: [
    "https://www.fyndanu.se/butik/e435da0b-10d0-46fa-a389-702295c89840",
    "https://www.blocket.se/butik/ak-husbil--fritid",
  ],
  knowsAbout: [
    "Fyndanu.se — svensk marknadsplats för husbilar och husvagnar",
    "Rapido husbilar",
    "Begagnade husbilar",
    "Husvagnar",
  ],
  makesOffer: [
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Husbilar" }, seller: { "@type": "Organization", name: "Fyndanu", url: "https://www.fyndanu.se" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Husvagnar" }, seller: { "@type": "Organization", name: "Fyndanu", url: "https://www.fyndanu.se" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Fritidstillbehör" } },
  ],
  subjectOf: {
    "@type": "WebPage",
    name: "AK Husbil & Fritid — officiell butik på Fyndanu",
    url: "https://www.fyndanu.se/butik/e435da0b-10d0-46fa-a389-702295c89840",
  },

};

const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "AK Husbil & Fritid",
  url: "https://www.akhusbilfritid.se/",
  inLanguage: "sv-SE",
  publisher: { "@id": "#organization" },
};

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#1a1410" },
      { name: "format-detection", content: "telephone=yes" },
      { name: "google-adsense-account", content: "ca-pub-7678537574282372" },
      { name: "google-site-verification", content: "r6u1aMkUYMO7l-O_nzanNSNWcGSi4TKJ_moOTF_nlfw" },
      { name: "author", content: "AK Husbil & Fritid" },
      // Standard för hela sajten. Enskilda rutter skriver över detta med noindex
      // (meta-taggar dedupliceras på name — barnrutten vinner).
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "AK Husbil & Fritid" },
      { property: "og:locale", content: "sv_SE" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/icon-192.png" },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "alternate", type: "application/rss+xml", title: "AK Husbil & Fritid – Blogg", href: "https://www.akhusbilfritid.se/rss.xml" },
      // hreflang sätts per rutt (svenska/finska-par) — se src/components/FiNav.tsx
      // Fonter är självhostade (inga render-blockerande Google Fonts-anrop)
      { rel: "preload", as: "font", type: "font/woff2", href: "/fonts/inter-latin.woff2", crossOrigin: "anonymous" },

      // AdSense / Google ads (laddas på de flesta sidor)
      { rel: "preconnect", href: "https://pagead2.googlesyndication.com" },
      { rel: "dns-prefetch", href: "https://pagead2.googlesyndication.com" },
      { rel: "preconnect", href: "https://googleads.g.doubleclick.net" },
      { rel: "dns-prefetch", href: "https://googleads.g.doubleclick.net" },
      // Fordonsbilder (Fyndanu-lagret) — LCP-kritiskt på fordonssidorna
      { rel: "preconnect", href: "https://bdvjinttdlvhwcnhfycc.supabase.co", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://bdvjinttdlvhwcnhfycc.supabase.co" },
      // Vårt eget backend (data, egna bilder)
      { rel: "preconnect", href: "https://wbhvakxggorqdhxrxypj.supabase.co", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://wbhvakxggorqdhxrxypj.supabase.co" },
    ],
    scripts: [
      // Strukturerad data först — viktigt för crawlers och kostar inget att parsa
      {
        type: "application/ld+json",
        children: JSON.stringify(ORGANIZATION_JSONLD),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(WEBSITE_JSONLD),
      },
      // AdSense-skriptet injiceras i klienten av <AutoAds /> först när sidan
      // klarar annonspolicyn (cookie-beslut, indexerbar sida, tillräckligt
      // innehåll) — se src/lib/ads-policy.ts.

      // Speculation Rules: webbläsaren förladdar (och för de viktigaste
      // ingångarna helt förrenderar) nästa sida innan klicket. Kombinerat med
      // vår edge-cache blir navigeringen i praktiken omedelbar. Privata och
      // muterande vägar exkluderas så inget körs i onödan.
      {
        type: "speculationrules",
        children: JSON.stringify({
          prerender: [
            {
              source: "list",
              urls: ["/husbilar-till-salu", "/husvagnar-till-salu", "/marknad", "/prisindex"],
              eagerness: "moderate",
            },
          ],
          prefetch: [
            {
              source: "document",
              eagerness: "moderate",
              where: {
                and: [
                  { href_matches: "/*" },
                  {
                    not: {
                      href_matches: [
                        "/admin*",
                        "/konto*",
                        "/login*",
                        "/auth*",
                        "/api/*",
                        "/sparade*",
                        "/forum/nytt*",
                        "/forum/sok*",
                      ],
                    },
                  },
                ],
              },
            },
          ],
        }),
      },
    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="sv">
      <head><HeadContent /></head>
      <body>{children}<Scripts /></body>
    </html>
  );
}

function AuthSync() {
  const router = useRouter();
  const qc = useQueryClient();
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      router.invalidate();
      qc.invalidateQueries();
    });
    return () => sub.subscription.unsubscribe();
  }, [router, qc]);
  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <AuthSync />
      <ScrollToTopOnNavigate />
      <AutoAds />
      <div className="flex min-h-screen flex-col">
        <a
          href="#innehall"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-full focus:bg-primary focus:px-5 focus:py-3 focus:text-sm focus:font-medium focus:text-primary-foreground"
        >
          Hoppa till innehållet
        </a>
        <SiteHeader />
        <SiteBreadcrumbs />
        <main id="innehall" className="flex-1"><Outlet /></main>
        <SiteFooter />
      </div>
      <ClientOnly fallback={null}><MobileCallBar /></ClientOnly>
      <CookieConsent />
      <InstallAppPrompt />
      <BackToTop />

      <ClientOnly fallback={null}>
        <Suspense fallback={null}><SecretHusbilParade /></Suspense>
      </ClientOnly>
      <Toaster richColors position="top-center" />
    </QueryClientProvider>
  );
}
